import { useEffect, useState } from 'react';
import Botao from '@/components/ui/Botao';
import CheckAnimado from '@/components/movimento/CheckAnimado';
import { formatarCentavos } from '@/pages/loja/utils';
import type { EstadoImpressao, VendaRegistrada } from '../types';

const ROTULO_METODO: Record<string, string> = {
  dinheiro: 'Dinheiro',
  pix: 'Pix',
  cartao: 'Cartão',
  debito: 'Débito',
  credito: 'Crédito',
};

interface Props {
  venda: VendaRegistrada;
  impressao: EstadoImpressao;
  /** Caixa com impressora: espera as fichas saírem antes de voltar sozinho. */
  imprimeCaixa: boolean;
  aoNovaVenda: () => void;
  aoReimprimir: () => void;
}

// Cartão de sucesso (✓ grande, nº, total, método, troco) + bloco das fichas.
// Volta sozinho para uma venda nova em 4 s quando tudo deu certo.
export default function TelaSucesso({
  venda,
  impressao,
  imprimeCaixa,
  aoNovaVenda,
  aoReimprimir,
}: Props) {
  const [restante, setRestante] = useState<number | null>(null);

  const fichasTotal = venda.fichas.reduce((soma, f) => soma + f.quantidade, 0);
  const r = impressao.resultado;
  const impressaoOk =
    !imprimeCaixa ||
    (!impressao.imprimindo &&
      r != null &&
      (r.tipo === 'ok' || r.tipo === 'nada' || r.tipo === 'a_caminho'));

  // Contagem da volta automática — só quando é seguro (nada pendente/aviso).
  useEffect(() => {
    if (impressaoOk) setRestante(3);
    else setRestante(null);
  }, [impressaoOk]);

  useEffect(() => {
    if (restante == null) return;
    if (restante <= 0) {
      aoNovaVenda();
      return;
    }
    const t = setTimeout(() => setRestante((x) => (x == null ? null : x - 1)), 1000);
    return () => clearTimeout(t);
  }, [restante, aoNovaVenda]);

  // Agrupa as fichas por barraca para conferência rápida.
  const grupos: { ponto: string; itens: { produto: string; quantidade: number }[] }[] = [];
  venda.fichas.forEach((f) => {
    let g = grupos.find((x) => x.ponto === f.ponto);
    if (!g) {
      g = { ponto: f.ponto, itens: [] };
      grupos.push(g);
    }
    g.itens.push({ produto: f.produto, quantidade: f.quantidade });
  });

  return (
    <div className="fixed inset-0 z-[65] flex items-start justify-center overflow-y-auto bg-background-100 px-4 py-6 sm:items-center">
      <div className="w-full max-w-[440px] overflow-hidden rounded-3xl border border-secondary-200 bg-white text-center">
        {/* Faixa verde do pagamento aprovado */}
        <div className="bg-green-600 px-6 py-6 text-white">
          <span className="mx-auto flex h-20 w-20 items-center justify-center rounded-full bg-white/20">
            <CheckAnimado
              tamanho={58}
              cor="#ffffff"
              fundo="rgba(255,255,255,0.14)"
            />
          </span>
          <p className="mt-3 text-5xl font-extrabold">PAGO</p>
          <p className="mt-1 text-2xl font-bold tabular-nums">
            {formatarCentavos(venda.total_centavos)} · {ROTULO_METODO[venda.metodo] ?? venda.metodo}
          </p>
          <p className="mt-0.5 text-sm">Venda #{venda.numero}</p>
        </div>

        <div className="p-6">
          {venda.troco_centavos > 0 && (
            <div className="rounded-2xl bg-secondary-50 px-4 py-3">
              <span className="block text-sm font-medium text-secondary-500">Troco</span>
              <span className="text-4xl font-extrabold tabular-nums text-foreground-950">
                {formatarCentavos(venda.troco_centavos)}
              </span>
            </div>
          )}

          {grupos.length > 0 && (
            <div className="mt-4 space-y-2 text-left">
              {grupos.map((g) => (
                <div key={g.ponto} className="rounded-2xl border border-secondary-100 px-4 py-2.5">
                  <p className="text-xs font-semibold uppercase tracking-wide text-secondary-500">
                    {g.ponto}
                  </p>
                  {g.itens.map((item, i) => (
                    <p key={i} className="text-xl font-extrabold text-foreground-900">
                      {item.quantidade}× {item.produto}
                    </p>
                  ))}
                </div>
              ))}
            </div>
          )}

          <BlocoImpressao
            impressao={impressao}
            fichasTotal={fichasTotal}
            aoReimprimir={aoReimprimir}
          />

          {restante != null && restante > 0 && (
            <p className="mt-4 text-sm text-secondary-500">
              Nova venda em {restante}…
            </p>
          )}

          <Botao variante="primario" tamanho="lg" cheio className="mt-3" onClick={aoNovaVenda}>
            Nova venda
          </Botao>
        </div>
      </div>
    </div>
  );
}

function BlocoImpressao({
  impressao,
  fichasTotal,
  aoReimprimir,
}: {
  impressao: EstadoImpressao;
  fichasTotal: number;
  aoReimprimir: () => void;
}) {
  if (impressao.imprimindo) {
    return (
      <div className="mt-4 rounded-2xl bg-primary-50 px-4 py-3 text-sm font-semibold text-primary-700">
        <span className="inline-flex items-center justify-center gap-2">
          <span className="flex h-5 w-5 items-center justify-center">
            <i className="ri-loader-4-line animate-spin text-lg" />
          </span>
          Imprimindo {impressao.total || fichasTotal} ficha(s)…
        </span>
        <p className="mt-1 text-xs font-medium text-primary-600">Olhe a maquininha.</p>
      </div>
    );
  }

  const r = impressao.resultado;
  if (!r) return null;

  if (r.tipo === 'nada') {
    return <p className="mt-4 text-sm text-secondary-500">Nada para imprimir.</p>;
  }

  if (r.tipo === 'a_caminho') {
    return (
      <div className="mt-4 flex items-center justify-center gap-2 rounded-2xl bg-success-50 px-4 py-3 text-sm font-semibold text-success-700">
        <span className="flex h-5 w-5 shrink-0 items-center justify-center">
          <i className="ri-printer-line text-lg" />
        </span>
        {r.total} ficha(s) a caminho — pegue na maquininha.
      </div>
    );
  }

  if (r.tipo === 'ok') {
    return (
      <div className="mt-4 flex items-center justify-center gap-2 rounded-2xl bg-success-50 px-4 py-3 text-sm font-semibold text-success-700">
        <span className="flex h-5 w-5 shrink-0 items-center justify-center">
          <i className="ri-checkbox-circle-line text-lg" />
        </span>
        {r.total} ficha(s) impressa(s). Entregue ao cliente.
      </div>
    );
  }

  const cor = r.tipo === 'erro' ? 'bg-danger-50 text-danger-700' : 'bg-warning-50 text-warning-800';
  const icone = r.tipo === 'erro' ? 'ri-error-warning-line' : 'ri-alert-line';

  return (
    <div className={`mt-4 rounded-2xl px-4 py-3 text-sm font-medium ${cor}`}>
      <p className="flex items-start justify-center gap-2 text-left">
        <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center">
          <i className={`${icone} text-lg`} />
        </span>
        <span>{r.mensagem}</span>
      </p>
      <Botao
        variante="secundario"
        tamanho="md"
        icone="ri-printer-line"
        className="mt-3 w-full"
        onClick={aoReimprimir}
      >
        Reimprimir
      </Botao>

      {impressao.itens.length > 0 && (
        <ul className="mt-3 space-y-1 text-left text-xs">
          {impressao.itens.map((it, i) => (
            <li key={i} className="flex items-center justify-between gap-2">
              <span className="truncate">Nº {it.numero}</span>
              <span className="shrink-0 opacity-80">{it.status}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}